import React, { useState } from "react";
import { Trash2, X, Download, CheckCircle2, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadStatic } from "@/lib/download";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert an upload path (`/api/uploads/<key>`) to a thumb URL of the given
 * width. Falls back to the original URL if the path isn't an uploads one
 * (e.g. external R2 URLs).
 */
function thumbUrl(url, w = 600) {
    if (!url) return url;
    const m = /\/api\/uploads\/([^/?#]+)$/.exec(url.split("?")[0]);
    if (!m) return url;
    return `/api/thumb/${m[1]}?w=${w}`;
}

/**
 * Selectable photo gallery — agent + admin can toggle a "Selecteer" mode,
 * tick photos, and bulk-delete via PATCH /api/requests/{id}/media/delete.
 * Optional download button per photo on hover.
 */
export default function PhotoGallery({
    requestId,
    photos,
    field, // "photo_urls" | "ai_photo_urls" | "document_urls"
    canDelete,
    onDeleted,
    testidPrefix = "photo",
    onPhotoClick, // optional — when provided, clicking a photo (outside select mode) fires this instead of opening a new tab
    badgeByIndex, // optional — (i) => ReactNode | null, rendered top-right on each tile
    canReorder = false, // optional — enables drag & drop reordering (ai_photo_urls only)
    onReordered, // optional — called after a successful reorder so parent reloads
    webSelection = null, // optional — array of urls in "Selectie website"; enables the globe toggle
    onToggleWeb, // optional — (url) => void, toggles membership of the website selection
}) {
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);
    const [reordering, setReordering] = useState(false);

    if (!photos || photos.length === 0) return null;

    const reorderEnabled = canReorder && !selectMode && photos.length > 1;

    const handleDrop = async (targetIdx) => {
        const from = dragIdx;
        setDragIdx(null);
        setOverIdx(null);
        if (from === null || from === targetIdx || reordering) return;
        const arr = [...photos];
        const [moved] = arr.splice(from, 1);
        arr.splice(targetIdx, 0, moved);
        setReordering(true);
        try {
            await api.post(`/requests/${requestId}/ai-photos/reorder`, { urls: arr });
            toast.success("Volgorde aangepast");
            onReordered?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Herordenen mislukt");
        }
        setReordering(false);
    };

    const toggle = (url) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(url)) n.delete(url); else n.add(url);
            return n;
        });
    };

    const exitSelect = () => {
        setSelectMode(false);
        setSelected(new Set());
    };

    const selectAll = () => {
        if (selected.size === photos.length) setSelected(new Set());
        else setSelected(new Set(photos));
    };

    const doDelete = async () => {
        if (selected.size === 0) return;
        setDeleting(true);
        try {
            const payload = { [field]: Array.from(selected) };
            const { data } = await api.patch(`/requests/${requestId}/media/delete`, payload);
            toast.success(`${data.deleted} foto${data.deleted !== 1 ? "'s" : ""} verwijderd`);
            onDeleted?.();
            exitSelect();
            setConfirmOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
        setDeleting(false);
    };

    return (
        <>
            {canDelete && (
                <div className="flex items-center gap-2 mb-3" data-testid={`${testidPrefix}-toolbar`}>
                    {!selectMode ? (
                        <button
                            onClick={() => setSelectMode(true)}
                            className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 border border-zinc-200 text-zinc-600 hover:border-[#E5A93D]/40 hover:text-zinc-950 rounded-sm transition"
                            data-testid={`${testidPrefix}-select-toggle`}
                        >
                            Selecteer
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={selectAll}
                                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 border border-zinc-200 text-zinc-600 hover:text-zinc-950 rounded-sm transition"
                                data-testid={`${testidPrefix}-select-all`}
                            >
                                {selected.size === photos.length ? "Deselecteer alles" : "Selecteer alles"}
                            </button>
                            <button
                                onClick={() => setConfirmOpen(true)}
                                disabled={selected.size === 0}
                                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 border border-red-500/40 text-red-600 hover:bg-red-500/10 rounded-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid={`${testidPrefix}-bulk-delete-btn`}
                            >
                                <Trash2 className="h-3 w-3" /> Verwijder {selected.size > 0 ? `(${selected.size})` : ""}
                            </button>
                            <button
                                onClick={exitSelect}
                                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 text-zinc-500 hover:text-zinc-950 rounded-sm transition"
                                data-testid={`${testidPrefix}-exit-select`}
                            >
                                <X className="h-3 w-3" /> Annuleer
                            </button>
                        </>
                    )}
                </div>
            )}
            {reorderEnabled && (
                <p className="text-[11px] text-zinc-400 mb-2" data-testid={`${testidPrefix}-reorder-hint`}>
                    Versleep foto's om de volgorde aan te passen — de beste eerst.
                </p>
            )}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {photos.map((u, i) => {
                    const filename = u.split("/").pop();
                    const isSelected = selected.has(u);
                    return (
                        <div
                            key={`${u}-${i}`}
                            className={`relative aspect-square rounded-sm overflow-hidden border group transition ${isSelected ? "border-[#E5A93D] ring-2 ring-[#E5A93D]/40" : "border-zinc-200 hover:border-[#E5A93D]/50"} ${selectMode || onPhotoClick ? "cursor-pointer" : ""} ${dragIdx === i ? "opacity-40" : ""} ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-[#E5A93D] border-[#E5A93D]" : ""} ${reordering ? "pointer-events-none opacity-60" : ""}`}
                            data-testid={`${testidPrefix}-${filename}`}
                            onClick={selectMode ? () => toggle(u) : (onPhotoClick ? () => onPhotoClick(i, u) : undefined)}
                            draggable={reorderEnabled}
                            onDragStart={reorderEnabled ? () => setDragIdx(i) : undefined}
                            onDragOver={reorderEnabled ? (e) => { e.preventDefault(); setOverIdx(i); } : undefined}
                            onDragLeave={reorderEnabled ? () => setOverIdx((v) => (v === i ? null : v)) : undefined}
                            onDrop={reorderEnabled ? (e) => { e.preventDefault(); handleDrop(i); } : undefined}
                            onDragEnd={reorderEnabled ? () => { setDragIdx(null); setOverIdx(null); } : undefined}
                        >
                            {selectMode ? (
                                <img src={`${BACKEND}${thumbUrl(u, 400)}`} alt="" loading="lazy" className={`w-full h-full object-cover transition ${isSelected ? "" : "opacity-70"}`} />
                            ) : onPhotoClick ? (
                                <img src={`${BACKEND}${thumbUrl(u, 400)}`} alt="" loading="lazy" className="w-full h-full object-cover" />
                            ) : (
                                <a href={`${BACKEND}${u}`} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                                    <img src={`${BACKEND}${thumbUrl(u, 400)}`} alt="" loading="lazy" className="w-full h-full object-cover" />
                                </a>
                            )}
                            {selectMode && (
                                <div
                                    className={`absolute top-1.5 left-1.5 h-6 w-6 rounded-sm flex items-center justify-center transition ${isSelected ? "bg-[#E5A93D] border border-[#E5A93D]" : "bg-black/60 border border-white/40 group-hover:border-white"}`}
                                    data-testid={`${testidPrefix}-checkbox-${filename}`}
                                >
                                    {isSelected && <CheckCircle2 className="h-4 w-4 text-black" />}
                                </div>
                            )}
                            {!selectMode && badgeByIndex && badgeByIndex(i)}
                            {!selectMode && webSelection && onToggleWeb && (() => {
                                const inWeb = webSelection.includes(u);
                                return (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleWeb(u); }}
                                        className={`absolute bottom-1 left-1 inline-flex items-center justify-center h-7 w-7 rounded-sm transition ${inWeb ? "bg-[#E5A93D] text-black shadow-[0_0_8px_rgba(229,169,61,0.6)]" : "bg-black/70 text-white opacity-0 group-hover:opacity-100 hover:bg-[#E5A93D] hover:text-black"}`}
                                        title={inWeb ? "In Selectie website — klik om te verwijderen" : "Voeg toe aan Selectie website"}
                                        data-testid={`${testidPrefix}-web-toggle-${filename}`}
                                    >
                                        <Globe className="h-3.5 w-3.5" />
                                    </button>
                                );
                            })()}
                            {!selectMode && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadStatic(u, filename); }}
                                    className="absolute bottom-1 right-1 inline-flex items-center justify-center h-7 w-7 rounded-sm bg-black/80 hover:bg-[#E5A93D] hover:text-black text-black opacity-0 group-hover:opacity-100 transition"
                                    title="Download foto"
                                    data-testid={`download-${testidPrefix}-${filename}`}
                                >
                                    <Download className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {confirmOpen && (
                <div className="fixed inset-0 z-[170] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !deleting && setConfirmOpen(false)} data-testid={`${testidPrefix}-confirm-modal`}>
                    <div className="bg-[#FFFFFF] border border-red-500/30 rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-2">
                            <Trash2 className="h-5 w-5 text-red-600" />
                            <h3 className="font-display font-bold text-xl">Foto's verwijderen?</h3>
                        </div>
                        <p className="text-sm text-zinc-500 mb-5">
                            Je staat op het punt <span className="text-zinc-950 font-medium">{selected.size} foto{selected.size !== 1 ? "'s" : ""}</span> definitief te verwijderen. Dit kan niet ongedaan gemaakt worden.
                        </p>
                        <div className="flex gap-2">
                            <Button onClick={() => setConfirmOpen(false)} disabled={deleting} variant="ghost" className="flex-1 h-10 border border-zinc-200 text-zinc-600 rounded-sm">
                                Annuleer
                            </Button>
                            <Button onClick={doDelete} disabled={deleting} className="flex-1 h-10 bg-red-500 hover:bg-red-400 text-zinc-950 font-bold rounded-sm active:scale-[0.98]" data-testid={`${testidPrefix}-confirm-delete-btn`}>
                                {deleting ? "Bezig..." : "Definitief verwijderen"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
